// Leaf imports (not the barrels): both source files are Angular/zef-free, so
// this decision module — and its unit test — stay pure. Going through the
// barrels would drag the zerops-services + auth-dialog Angular graphs in.
import { SUPPORTED_AGENT_TYPES, ZagentAgentType } from '@zerops/zerops/core/zerops-services/zerops-services.model';
import { hasAuthHandler } from '@zerops/zerops/feature/zcp-agent-auth-dialog/zcp-agent-auth-dialog.handlers';

export const AGENT_AUTH_BRIDGE_CHANNEL = '@zerops/zcp-agent-auth-bridge';
export const AGENT_AUTH_BRIDGE_ACK_TYPE = 'open-agent-auth-ack';
export const AGENT_AUTH_BRIDGE_EVENT_TTL_MS = 30_000;

// `createdAt` is stamped by the SENDING browser context immediately before
// posting — the webview for every embed→FE type here, the FE page itself for
// FE→embed sends (spec-welcome-mode.md §4.1). The extension host never stamps
// it, so there is no container-clock skew source left to correct for: every
// browser context on the page shares one clock. The tolerance below stays as
// defense in depth — a delivery hop or a rounding edge could still put a
// freshly-minted event's `createdAt` a few ms into the future relative to the
// moment this runs, and without it that turns into timing-dependent
// randomness (fast messages rejected, slow ones accepted as the clock catches
// up). The TTL still bounds the past side.
export const AGENT_AUTH_BRIDGE_MAX_FUTURE_SKEW_MS = 5_000;

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Why a structurally/freshness/identity-valid message was still dropped. The
// wire stays silent for every one of these (a probing frame gets no oracle);
// the reason exists ONLY so the component can emit a local console.debug line.
export type AgentAuthBridgeIgnoreReason =
  | 'bad-envelope'
  | 'context-missing'
  | 'origin-mismatch'
  | 'source-not-current'
  | 'future-skew'
  | 'expired'
  | 'replay';

// Reasons a rejecting ack can carry back to the sender. `unsupported-agent`:
// structurally valid but the agent has no auth handler. `not-ready`: accepted,
// dialog-open dispatched, but the container never became resolvable so the
// dialog didn't actually open (see the truthful-ack flow in the component).
export type AgentAuthBridgeAckReason = 'unsupported-agent' | 'not-ready';

export interface AgentAuthBridgeMessage {
  channel: typeof AGENT_AUTH_BRIDGE_CHANNEL;
  version: 1;
  type: 'open-agent-auth';
  agentType: string;
  eventId: string;
  createdAt: number;
}

export interface AgentAuthBridgeAckMessage {
  channel: typeof AGENT_AUTH_BRIDGE_CHANNEL;
  version: 1;
  type: typeof AGENT_AUTH_BRIDGE_ACK_TYPE;
  eventId: string;
  accepted: boolean;
  reason?: AgentAuthBridgeAckReason;
}

// Embed command channel (embed→FE, spec-welcome-mode.md §4.3): the onboarding
// handshake riding the SAME channel/version as the auth trigger above — one
// contract, one pipeline, dispatched by `type`.

/** One roster entry in an `embed-ready` announce. No `installed` axis — see §4.3. */
export interface EmbedReadyAgentEntry {
  id: string;
  authorized: boolean;
}

export interface EmbedReadyBridgeMessage {
  channel: typeof AGENT_AUTH_BRIDGE_CHANNEL;
  version: 1;
  type: 'embed-ready';
  eventId: string;
  createdAt: number;
  agents: EmbedReadyAgentEntry[];
  bootstrapVersion: string;
}

/**
 * The envelope `eventId` IS the correlated `launch-agent` command's eventId —
 * per §4.3 an outcome carries no identity of its own, so there is no separate
 * correlation field.
 */
export interface AgentReadyBridgeMessage {
  channel: typeof AGENT_AUTH_BRIDGE_CHANNEL;
  version: 1;
  type: 'agent-ready';
  eventId: string;
  createdAt: number;
  agentId: string;
}

export type LaunchFailedReason = 'unknown-agent' | 'terminal-error';

export interface LaunchFailedBridgeMessage {
  channel: typeof AGENT_AUTH_BRIDGE_CHANNEL;
  version: 1;
  type: 'launch-failed';
  eventId: string;
  createdAt: number;
  agentId: string;
  reason: LaunchFailedReason;
}

export type EmbedWizardMode = 'onboarding' | 'standard';

/** FE→embed directive (§4.3). Idempotent; re-sent on every `embed-ready`. */
export interface SetModeBridgeMessage {
  channel: typeof AGENT_AUTH_BRIDGE_CHANNEL;
  version: 1;
  type: 'set-mode';
  eventId: string;
  createdAt: number;
  mode: EmbedWizardMode;
}

// Identity + origin for the currently embedded iframe. Captured atomically
// (all three fields from one emission of the URL-resolution stream — never
// combined from independently-latest signals) because the overlay's meta can
// change while it stays open (re-subscribed per open→close→open cycle) — a
// stale origin must never pair with a newer stack's identity, or vice versa.
export interface AgentAuthBridgeContext {
  origin: string;
  serviceStackId: string;
  clientId: string;
}

/**
 * An agentType is bridge-supported iff BOTH capability truths agree: it is a
 * UI-offered agent (`SUPPORTED_AGENT_TYPES`) AND the dialog has an auth handler
 * for it (`hasAuthHandler`). Derived from those two sources — never a hardcoded
 * list — so a widened UI paired with a new handler becomes bridge-reachable
 * automatically, while an agent that is offered but unhandled (or handled but
 * not offered) can never open the auth dialog into a dead end.
 */
export function isBridgeSupportedAgentType(agentType: string): agentType is ZagentAgentType {
  return (SUPPORTED_AGENT_TYPES as readonly string[]).includes(agentType)
    && hasAuthHandler(agentType as ZagentAgentType);
}

export interface AgentAuthBridgeValidationInput {
  /** Raw `MessageEvent.data` — untrusted, validated structurally here. */
  data: unknown;
  /** `MessageEvent.origin` — the browser-authoritative sender origin. */
  origin: string;
  /** Identity + origin of the currently embedded iframe, or null if none. */
  context: AgentAuthBridgeContext | null;
  /**
   * Whether the message's source window is (or is nested under) the current
   * iframe. A thunk, not a plain boolean: the check is a DOM walk (up to 8
   * cross-window parent hops) and must only actually run for a message that
   * already cleared the cheap envelope/context/origin checks below — never
   * spent on a message that would be rejected before reaching it anyway.
   * Computed by the caller — walking the window parent chain needs live
   * `Window` objects and can't be made pure.
   */
  fromCurrentIframe: () => boolean;
  /** Lowercased event ids already consumed (replay guard). */
  seenEventIds: ReadonlySet<string>;
  /** Current wall-clock time (`Date.now()`), for the TTL check. */
  now: number;
}

export type AgentAuthBridgeVerdict =
  // Silent drop — no ack. A structural / origin / iframe-identity / freshness
  // failure or a replay: a probing frame gets no oracle for why it was dropped.
  // `reason` (+ `eventId`/`ageMs` when the envelope parsed far enough to know
  // them) is for the component's local diagnostic log only — never the wire.
  | {
      verdict: 'ignore';
      reason: AgentAuthBridgeIgnoreReason;
      eventId?: string;
      ageMs?: number;
    }
  // Structurally valid, fresh, unseen — but the agent is not bridge-supported.
  // Still acked (rejecting) so the sender doesn't hang on its own timeout.
  | { verdict: 'unsupported'; eventId: string }
  // Accept: dispatch the auth dialog. `serviceStackId`/`clientId` come from the
  // validated CONTEXT, never the message; `agentType` is the message's, verbatim.
  | {
      verdict: 'accept';
      eventId: string;
      agentType: ZagentAgentType;
      serviceStackId: string;
      clientId: string;
    }
  // The three embed command-channel types (§4.3) — no support/unsupported
  // gate; each resolves straight to its own verdict once the shared pipeline
  // above clears. Payload fields are the message's, verbatim.
  | { verdict: 'embed-ready'; eventId: string; agents: EmbedReadyAgentEntry[]; bootstrapVersion: string }
  | { verdict: 'agent-ready'; eventId: string; agentId: string }
  | { verdict: 'launch-failed'; eventId: string; agentId: string; reason: LaunchFailedReason };

/** Structural shape check for an `embed-ready` payload's `agents` field. */
function isValidEmbedReadyAgents(value: unknown): value is EmbedReadyAgentEntry[] {
  return Array.isArray(value) && value.every((entry) =>
    !!entry
    && typeof entry === 'object'
    && typeof (entry as Record<string, unknown>).id === 'string'
    && typeof (entry as Record<string, unknown>).authorized === 'boolean'
  );
}

/**
 * Pure decision for an inbound bridge postMessage — any of the channel's four
 * known types (`open-agent-auth`, and the embed command-channel's
 * `embed-ready` / `agent-ready` / `launch-failed`, §4.3): no DOM, no signals,
 * no mutation — every input is plain data, so the decision is unit-testable
 * in isolation. The component keeps every side effect (dedup-set mutation,
 * iframe-window walk, ack/set-mode postMessage, action dispatch) and
 * delegates the verdict here.
 *
 * Every inbound type passes the SAME structural / origin / iframe-identity /
 * freshness / replay pipeline before earning any verdict but `ignore` — only
 * the terminal payload differs by type: `open-agent-auth` additionally gates
 * on agent support (`unsupported` | `accept`); the three embed command-channel
 * types have no such gate and resolve straight to their own verdict once the
 * shared pipeline clears. An unrecognized `type`, or a recognized type whose
 * payload doesn't match its expected shape, is `bad-envelope` — the same
 * bucket a malformed `open-agent-auth` message already fell into.
 */
export function validateAgentAuthBridgeMessage(
  input: AgentAuthBridgeValidationInput
): AgentAuthBridgeVerdict {
  const { data, origin, context, fromCurrentIframe, seenEventIds, now } = input;
  const message = data as Record<string, unknown> | null;

  // Shared envelope shape first: a malformed message has no trustworthy
  // eventId/createdAt to attribute later reasons to, so it can only ever be
  // `bad-envelope`.
  if (
    !message
    || typeof message !== 'object'
    || message.channel !== AGENT_AUTH_BRIDGE_CHANNEL
    || message.version !== 1
    || typeof message.eventId !== 'string'
    || !UUID_V4.test(message.eventId)
    || typeof message.createdAt !== 'number'
    || !Number.isFinite(message.createdAt)
  ) {
    return { verdict: 'ignore', reason: 'bad-envelope' };
  }

  // From here the eventId/createdAt are known-good, so every ignore below
  // carries the eventId.
  const eventId = message.eventId;
  const createdAt = message.createdAt;
  const type = message.type;

  // Per-type payload shape — still `bad-envelope` on mismatch.
  if (type === 'open-agent-auth') {
    if (typeof message.agentType !== 'string') {
      return { verdict: 'ignore', reason: 'bad-envelope' };
    }
  } else if (type === 'embed-ready') {
    if (!isValidEmbedReadyAgents(message.agents) || typeof message.bootstrapVersion !== 'string') {
      return { verdict: 'ignore', reason: 'bad-envelope' };
    }
  } else if (type === 'agent-ready') {
    if (typeof message.agentId !== 'string') {
      return { verdict: 'ignore', reason: 'bad-envelope' };
    }
  } else if (type === 'launch-failed') {
    if (
      typeof message.agentId !== 'string'
      || (message.reason !== 'unknown-agent' && message.reason !== 'terminal-error')
    ) {
      return { verdict: 'ignore', reason: 'bad-envelope' };
    }
  } else {
    return { verdict: 'ignore', reason: 'bad-envelope' };
  }

  // Shared context / origin / iframe-identity / freshness / replay pipeline —
  // identical for every inbound type; only the terminal verdict differs.
  if (!context) {
    return { verdict: 'ignore', reason: 'context-missing', eventId };
  }
  if (origin !== context.origin) {
    return { verdict: 'ignore', reason: 'origin-mismatch', eventId };
  }
  if (!fromCurrentIframe()) {
    return { verdict: 'ignore', reason: 'source-not-current', eventId };
  }

  const age = now - createdAt;
  // Bounded future skew is tolerated; a larger lead — or a stale event past
  // the TTL — is dropped.
  if (age < -AGENT_AUTH_BRIDGE_MAX_FUTURE_SKEW_MS) {
    return { verdict: 'ignore', reason: 'future-skew', eventId, ageMs: age };
  }
  if (age > AGENT_AUTH_BRIDGE_EVENT_TTL_MS) {
    return { verdict: 'ignore', reason: 'expired', eventId, ageMs: age };
  }
  if (seenEventIds.has(eventId.toLowerCase())) {
    return { verdict: 'ignore', reason: 'replay', eventId, ageMs: age };
  }

  // Every shared check passed — dispatch to the type-specific terminal verdict.
  if (type === 'open-agent-auth') {
    const agentType = message.agentType as string;
    if (!isBridgeSupportedAgentType(agentType)) {
      return { verdict: 'unsupported', eventId };
    }
    return {
      verdict: 'accept',
      eventId,
      agentType,
      serviceStackId: context.serviceStackId,
      clientId: context.clientId
    };
  }
  if (type === 'embed-ready') {
    return {
      verdict: 'embed-ready',
      eventId,
      agents: message.agents as EmbedReadyAgentEntry[],
      bootstrapVersion: message.bootstrapVersion as string
    };
  }
  if (type === 'agent-ready') {
    return { verdict: 'agent-ready', eventId, agentId: message.agentId as string };
  }
  // launch-failed — the only remaining branch after the shape check above.
  return {
    verdict: 'launch-failed',
    eventId,
    agentId: message.agentId as string,
    reason: message.reason as LaunchFailedReason
  };
}

/**
 * Ack outcome for a deferred (accept-path) bridge request once the dialog-open
 * effect reports back whether the container actually resolved. Pure so the
 * truthful-ack decision is unit-testable in isolation: `ok` acks acceptance,
 * `!ok` acks a rejecting `not-ready` (the sender learns the dialog didn't open
 * rather than hanging on its own timeout). The component owns the pending-map
 * lookup and the postMessage; the mapping from result → ack payload lives here.
 */
export function ackOutcomeForManualOpenResult(
  ok: boolean
): { accepted: boolean; reason?: AgentAuthBridgeAckReason } {
  return ok ? { accepted: true } : { accepted: false, reason: 'not-ready' };
}

/**
 * Builds the FE→embed `set-mode` envelope. Pure: `eventId`/`createdAt` are
 * supplied by the caller (`crypto.randomUUID()`/`Date.now()` at send time) so
 * this stays unit-testable without touching global browser APIs. Unlike
 * `launch-agent`'s retried intent, `set-mode` has no cross-message identity to
 * preserve — every send, including a re-send on a later `embed-ready`, mints
 * its own fresh `eventId` (§4.1).
 */
export function buildSetModeMessage(
  mode: EmbedWizardMode,
  eventId: string,
  createdAt: number
): SetModeBridgeMessage {
  return {
    channel: AGENT_AUTH_BRIDGE_CHANNEL,
    version: 1,
    type: 'set-mode',
    eventId,
    createdAt,
    mode
  };
}
